import type {
  readFirstCookie,
  readStoredPostHogIdentity,
  SettledVisitorId,
  settleVisitorId,
} from '@/lib/analytics/visitor-id';
import type { ServerFlags } from '@/lib/flags/types';

export interface EarlyAnalyticsConfig {
  apiKey: string;
  /** posthog's localStorage entry and cookie name, which hold the visitor's id. */
  persistenceKey: string;
  /** Where `/api/embed.js` (and the proxy) mint an anonymous id for a cold visitor. */
  bootstrapCookieName: string;
  /** URL parameters the start-up module removes, so no report carries them either. */
  privateParams: string[];
  environment: string;
  /** Sent as `tenantKey` and `tenant`, the super properties the chat registers once it mounts. */
  tenantKey: string;
  /** Off on a developer's own machine: production error tracking pages people. */
  reportErrors: boolean;
  flags: ServerFlags | null;
  /** Every visitor flag at its default, which posthog starts from where the page's flags don't
   *  stand and it asks for none: it replaces flags posthog stored on an earlier visit. */
  flagDefaults: ServerFlags['values'];
}

export interface EarlyBootstrapParams {
  config: EarlyAnalyticsConfig;
  settleVisitorId: typeof settleVisitorId;
  readStoredPostHogIdentity: typeof readStoredPostHogIdentity;
  readFirstCookie: typeof readFirstCookie;
}

export interface EarlyErrorReport {
  error: unknown;
  /** Epoch ms of when the page threw or caught it. */
  at: number;
  /** The chat caught and reported it itself; uncaught errors have no properties. */
  handled: boolean;
  properties: Record<string, unknown>;
}

export type ReportEarlyError = (report: EarlyErrorReport) => void;

export interface HeldEvent {
  event: string;
  properties: Record<string, unknown>;
  /** Epoch ms of when it happened, sent as the event's own time. */
  at: number;
  uuid: string;
}

export interface DrainedAnalytics {
  /** The page view already went out by beacon; the start-up script pairs its page leave. */
  pageviewBeaconed: boolean;
  /** The URL, referrer and campaign the visitor arrived with: a session's entry and channel are
   *  read from its earliest event, which may be one of these rather than the page view. */
  arrival: Record<string, unknown>;
  registered: Record<string, unknown>;
  /** The page view, unless it already went out by beacon. */
  pageview: HeldEvent | null;
  events: HeldEvent[];
}

/** Marks an error this script reported, with when it happened (epoch ms). */
export const EARLY_ERROR_AT = 'earlyErrorAt';

declare global {
  interface Window {
    __chatEarly?: {
      tenantKey: string;
      flags: ServerFlags | null;
      flagDefaults: ServerFlags['values'];
      /** Reports the held errors through `report`, then every later one until posthog's own
       *  error capture is running. */
      handOver: (report: ReportEarlyError) => void;
      /** The visitor id this page settles on, decided once on first use. */
      visitor: () => SettledVisitorId;
      session: () => { id: string; minted: boolean };
      capture: (event: string, properties?: Record<string, unknown>) => void;
      register: (properties: Record<string, unknown>) => void;
      captureException: (error: unknown, properties?: Record<string, unknown>) => void;
      /** posthog has started: everything held goes to it, and nothing more goes out by beacon. A
       *  page first shown after this hands its page view to `onLatePageview`. */
      drain: (onLatePageview: (pageview: HeldEvent) => void) => DrainedAnalytics;
    };
  }
}
